import React, { useState, useEffect, useMemo } from 'react';
import {
  Send, Loader2, Users, History, ChevronDown, ChevronUp, Check, AlertTriangle,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import ReactMarkdown from 'react-markdown';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NewsletterTab = ({ password }) => {
  const [recipients, setRecipients] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [openHistoryId, setOpenHistoryId] = useState(null);

  const headers = useMemo(() => ({ 'x-admin-password': password }), [password]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [recRes, hisRes] = await Promise.all([
        fetch(`${API}/admin/newsletter/recipients`, { headers }),
        fetch(`${API}/admin/newsletter/history`, { headers }),
      ]);
      const recData = await recRes.json();
      const hisData = await hisRes.json();
      setRecipients(recData.recipients || []);
      setHistory(hisData.history || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, []);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const selectAll = () => setSelectedIds(new Set(recipients.map((r) => r.id)));
  const selectNone = () => setSelectedIds(new Set());

  const handleSend = async () => {
    if (selectedIds.size === 0) {
      alert('Bitte mindestens einen Empfänger auswählen.');
      return;
    }
    if (subject.trim().length < 3 || body.trim().length < 10) {
      alert('Betreff (≥ 3) und Inhalt (≥ 10 Zeichen) erforderlich.');
      return;
    }
    if (!window.confirm(
      `Newsletter an ${selectedIds.size} Empfänger versenden?`
    )) return;

    setSending(true);
    setResult(null);
    try {
      const res = await fetch(`${API}/admin/newsletter/send`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subject: subject.trim(),
          body_markdown: body,
          recipient_ids: Array.from(selectedIds),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Versand fehlgeschlagen');
      setResult(data);
      setSubject('');
      setBody('');
      setSelectedIds(new Set());
      fetchData();
    } catch (err) {
      alert(err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="newsletter-tab">
      <div>
        <h2 className="text-lg font-semibold text-slate-900">Newsletter-Versand</h2>
        <p className="text-sm text-slate-500 mt-1">
          Versand an Whitepaper-Leads (DSGVO: alle haben aktiv zugestimmt). Pro Versand wählst du
          die Empfänger einzeln aus.
        </p>
      </div>

      {result && (
        <div
          className={`rounded-lg border p-3 flex items-center gap-2 text-sm ${
            (result.failed?.length || 0) === 0
              ? 'bg-green-50 border-green-200 text-green-800'
              : 'bg-amber-50 border-amber-200 text-amber-800'
          }`}
          data-testid="newsletter-result"
        >
          {result.failed?.length ? <AlertTriangle size={16} /> : <Check size={16} />}
          <span>
            {result.sent} Mail(s) versendet
            {result.failed?.length ? ` · ${result.failed.length} fehlgeschlagen` : ''}.
          </span>
        </div>
      )}

      {/* Composer */}
      <div className="bg-white rounded-lg border border-slate-200 p-5 shadow-sm space-y-3">
        <h3 className="font-semibold text-slate-900">Neuen Newsletter verfassen</h3>
        <Input
          placeholder="Betreff (max 160 Zeichen)"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          maxLength={160}
          data-testid="newsletter-subject-input"
        />
        <Textarea
          placeholder={
            "# Überschrift\n\nKurzer Einleitungstext.\n\n## Abschnitt\n\n- Punkt 1\n- Punkt 2\n\n[Mehr lesen](https://smart-post-solution.de)"
          }
          value={body}
          onChange={(e) => setBody(e.target.value)}
          className="min-h-[200px] font-mono text-sm"
          data-testid="newsletter-body-input"
        />
        <p className="text-xs text-slate-500">
          Markdown unterstützt: `# Überschrift`, `## Subhead`, `- Bullet`, `**fett**`, `*kursiv*`, `[Link](https://…)`.
        </p>
      </div>

      {/* Recipients */}
      <div className="bg-white rounded-lg border border-slate-200 p-5 shadow-sm space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h3 className="font-semibold text-slate-900 flex items-center gap-2">
            <Users size={16} className="text-slate-600" />
            Empfänger ({recipients.length})
            <span className="text-xs text-slate-500 font-normal">
              · {selectedIds.size} ausgewählt
            </span>
          </h3>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={selectAll} disabled={recipients.length === 0}>
              Alle auswählen
            </Button>
            <Button variant="outline" size="sm" onClick={selectNone} disabled={selectedIds.size === 0}>
              Auswahl löschen
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-6">
            <Loader2 className="animate-spin text-slate-400" size={22} />
          </div>
        ) : recipients.length === 0 ? (
          <p className="text-sm text-slate-400 italic">
            Noch keine Whitepaper-Leads. Sobald Interessenten das Whitepaper anfordern,
            erscheinen sie hier.
          </p>
        ) : (
          <div className="max-h-[260px] overflow-y-auto border border-slate-100 rounded">
            <table className="w-full text-sm">
              <tbody>
                {recipients.map((r) => (
                  <tr
                    key={r.id}
                    className="border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors"
                  >
                    <td className="px-3 py-2 w-8">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(r.id)}
                        onChange={() => toggleSelect(r.id)}
                        className="cursor-pointer"
                        data-testid={`newsletter-recipient-${r.id}`}
                      />
                    </td>
                    <td className="px-3 py-2 text-slate-800 font-medium">{r.name}</td>
                    <td className="px-3 py-2 text-slate-500 text-xs">{r.email}</td>
                    <td className="px-3 py-2 text-slate-500 text-xs">{r.company || '–'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="pt-2">
          <Button
            onClick={handleSend}
            disabled={sending || selectedIds.size === 0}
            className="text-white disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              backgroundColor: selectedIds.size === 0 ? '#cbd5e1' : '#FFA266',
            }}
            data-testid="newsletter-send-btn"
          >
            {sending ? (
              <>
                <Loader2 size={14} className="mr-2 animate-spin" />
                Versende…
              </>
            ) : (
              <>
                <Send size={14} className="mr-2" />
                An {selectedIds.size || 0} Empfänger senden
              </>
            )}
          </Button>
        </div>
      </div>

      {/* History */}
      <div className="bg-white rounded-lg border border-slate-200 p-5 shadow-sm">
        <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
          <History size={16} className="text-slate-600" />
          Versand-Historie ({history.length})
        </h3>
        {history.length === 0 ? (
          <p className="text-sm text-slate-400 italic">Noch keine Versände.</p>
        ) : (
          <ul className="space-y-2">
            {history.map((h) => {
              const open = openHistoryId === h.id;
              return (
                <li key={h.id} className="border border-slate-200 rounded-md overflow-hidden">
                  <button
                    onClick={() => setOpenHistoryId(open ? null : h.id)}
                    className="w-full px-3 py-2 bg-slate-50 hover:bg-slate-100 flex items-center justify-between text-left"
                  >
                    <span className="flex items-center gap-2 text-sm font-medium text-slate-800">
                      {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                      {h.subject}
                    </span>
                    <span className="text-xs text-slate-500">
                      {new Date(h.created_at).toLocaleString('de-DE')} · {h.sent} versandt
                      {h.failed?.length ? ` · ${h.failed.length} Fehler` : ''}
                    </span>
                  </button>
                  {open && (
                    <div className="px-4 py-3 markdown-briefing border-t border-slate-200">
                      <ReactMarkdown>{h.body_markdown}</ReactMarkdown>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default NewsletterTab;
